import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import type { Dimension } from '@/graphql/brokerage-settings';

import styles from './DimensionForm.module.css';

export type Outcome = 'ONGOING' | 'SUCCESS' | 'FAILURE';

export interface DimensionDraft {
  label: string;
  description: string | null;
  color?: string | null;
  outcome?: Outcome;
}

export interface DimensionFormProps {
  singular: string;
  /** Null to add a new one. */
  item: Dimension | null;
  hasOutcome: boolean;
  busy?: boolean;
  error?: string | null;
  onSubmit: (draft: DimensionDraft) => void;
  onCancel: () => void;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function outcomeOf(item: Dimension | null): Outcome {
  if (item?.isTerminalSuccess) {
    return 'SUCCESS';
  }
  return item?.isTerminalFailure ? 'FAILURE' : 'ONGOING';
}

/** Add or change one stage, source, task type or task status. */
export function DimensionForm({
  singular,
  item,
  hasOutcome,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: DimensionFormProps) {
  const [label, setLabel] = useState(item?.label ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [color, setColor] = useState(item?.color ?? '');
  const [outcome, setOutcome] = useState<Outcome>(outcomeOf(item));
  const badColor = color.trim() !== '' && !HEX.test(color.trim());

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      label: label.trim(),
      description: description.trim() || null,
      ...(hasOutcome ? { color: color.trim() || null, outcome } : {}),
    });
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} type="button" variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={!label.trim() || badColor}
            form="dimension-form"
            loading={busy}
            type="submit"
          >
            {item ? 'Save' : `Add ${singular}`}
          </Button>
        </>
      }
      description={item ? 'The main app shows the change straight away.' : undefined}
      dismissable={!busy}
      onClose={onCancel}
      title={item ? `Edit ${item.label ?? singular}` : `New ${singular}`}
    >
      <form className={styles.form} id="dimension-form" onSubmit={submit}>
        <TextField
          disabled={busy}
          label="Name"
          onChange={(e) => setLabel(e.target.value)}
          required
          value={label}
        />
        <TextField
          disabled={busy}
          label="Description"
          onChange={(e) => setDescription(e.target.value)}
          value={description}
        />
        {hasOutcome ? (
          <>
            <div className={styles.color}>
              <TextField
                disabled={busy}
                error={badColor ? 'Use a colour like #1aa36f' : undefined}
                label="Colour"
                onChange={(e) => setColor(e.target.value)}
                placeholder="#1aa36f"
                value={color}
              />
              <input
                aria-label="Pick a colour"
                className={styles.picker}
                disabled={busy}
                onChange={(e) => setColor(e.target.value)}
                type="color"
                value={HEX.test(color) && color.length === 7 ? color : '#888888'}
              />
            </div>
            <Select
              disabled={busy}
              label="When something reaches it"
              onChange={(value) => setOutcome(value as Outcome)}
              options={[
                ['ONGOING', 'It is still in progress'],
                ['SUCCESS', 'It has ended successfully'],
                ['FAILURE', 'It has ended unsuccessfully'],
              ]}
              value={outcome}
            />
          </>
        ) : null}
        {item?.key ? (
          <p className={styles.key}>Reference: {item.key} (this never changes)</p>
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't save">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
